<template>
  <v-tab-item :value="$route.path" class="pa-3">
    <v-toolbar flat height="90">
      <v-btn
        color="secondary"
        class="text-capitalize px-5"
        depressed
        v-text="translations.add[lang]"
        @click="openAttributeOptionDialog('create', {})"
      ></v-btn>
    </v-toolbar>
    <v-row no-gutters justify="end" class="px-4 pb-3 align-end">
      <div class="mr-auto"></div>

      <v-col cols="4" class="pa-0">
        <b-search-field v-model="search"></b-search-field>
      </v-col>
    </v-row>
    <b-data-table
      :headers="headers"
      :items="attributeOptions"
      :page.sync="page"
      @page-sync="(p) => (page = p)"
      :loading="loading"
      disable-sort
      class="b-outlined"
    >
      <template v-slot:item.attribute_id="{ item }">
        <span> {{ getAttributeName(item.attribute_id) }}</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-tooltip color="secondary" top>
          <template v-slot:activator="{ on }">
            <v-btn
              color="yellow darken-3"
              icon
              v-on="on"
              @click="openAttributeOptionDialog('update', item)"
            >
              <v-icon v-text="icons.mdiPencilOutline"></v-icon>
            </v-btn>
          </template>

          <span class="font-weight-bold" v-text="translations.update[lang]"></span>
        </v-tooltip>

        <v-tooltip color="secondary" top>
          <template v-slot:activator="{ on }">
            <v-btn
              color="red"
              icon
              v-on="on"
              @click="
                () => {
                  attributeOption = item;
                  deleteDialog = true;
                }
              "
            >
              <v-icon v-text="icons.mdiClose"></v-icon>
            </v-btn>
          </template>

          <span class="font-weight-bold" v-text="translations.delete[lang]"></span>
        </v-tooltip>
      </template>
    </b-data-table>

    <v-dialog v-model="attributeOptionDialog" max-width="600">
      <b-card
        :title="translations[`${mode}`][this.lang]"
        :loading="loading"
        :error-message="errorMessage"
        @cancel="closeForm"
        @submit="submitForm"
        :submit-text="{ el: 'Αποθήκευση', en: 'Save', it: 'Salva' }"
        :cancel-text="{ el: 'Ακύρωση', en: 'Cancel', it: 'Cancella' }"
      >
        <b-text-field v-model="attributeOption.option[nameLang]" :label="translations.name[lang]">
          <template v-slot:append>
            <b-lang-menu v-model="nameLang" type="inner"></b-lang-menu>
          </template>
        </b-text-field>

        <b-select
          :items="attributes"
          :item-text="`name[${lang}]`"
          item-value="attribute_id"
          v-model="attributeOption.attribute_id"
          :label="translations.attribute[lang]"
        ></b-select>
      </b-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500">
      <b-card
        type="delete"
        :title="translations.delete[lang]"
        :submit-text="translations.delete"
        :loading="loading"
        :error-message="errorMessage"
        @cancel="deleteDialog = false"
        @submit="deleteItem()"
      >
        <div class="subtitle-1 font-weight-medium pt-2 px-2">
          {{ translations.deleteConfirmationMessage[lang] }}
          <span class="font-weight-bold text--primary">{{ attributeOption.option[lang] }}</span
          >{{ translations.questionmark[lang] }}
        </div>
      </b-card>
    </v-dialog>
  </v-tab-item>
</template>

<script>
import { mdiPencilOutline, mdiClose } from '@mdi/js';

import { mapState, mapMutations, mapActions } from 'vuex';
import debounce from 'lodash/debounce';
import translations from '@/utils/translations/general/settings/roadcubeMarket';
import { cloneDeep } from 'lodash';

export default {
  name: 'AttributeOptions',

  mixins: [translations],

  data() {
    return {
      icons: { mdiPencilOutline, mdiClose },
      page: +this.$route.query.page,
      mode: 'create',
      search: '',
      attributeOptionDialog: false,
      deleteDialog: false,
      nameLang: this.$route.params.lang,
      changeNameElAuto: true,
      changeNameItAuto: true,
      changeNameEnAuto: true,
      descriptionLang: this.$route.params.lang,
      changeDescriptionElAuto: true,
      changeDescriptionItAuto: true,
      changeDescriptionEnAuto: true,
      attributeOptionLang: this.$route.params.lang,
    };
  },

  computed: {
    ...mapState(['loading', 'errorMessage']),

    ...mapState('general/settings/roadcubeMarket/attributeOptions', [
      'attributeOptions',
      'attributeOption',
    ]),
    ...mapState('general/settings/roadcubeMarket/attributes', ['attributes']),

    lang() {
      return this.$route.params.lang;
    },

    headers() {
      return [
        {
          text: this.translations.name[this.lang],
          value: `option['${this.lang}']`,
        },
        {
          text: this.translations.attribute[this.lang],
          value: `attribute_id`,
        },
        {
          text: this.translations.actions[this.lang],
          value: 'actions',
        },
      ];
    },

    attributeOption: {
      get() {
        return this.$store.state.general.settings.roadcubeMarket.attributeOptions.attributeOption;
      },

      set(val) {
        this.setAttributeOption(val);
      },
    },

    query() {
      let query = '?';

      for (let key in this.$route.query) {
        query += `${key}=${this.$route.query[key]}&`;
      }

      return query.slice(0, query.length - 1);
    },
  },

  methods: {
    ...mapMutations('general/settings/roadcubeMarket/attributeOptions', ['setAttributeOption']),
    ...mapActions('general/settings/roadcubeMarket/attributeOptions', [
      'getAttributeOptions',
      'create',
      'update',
      'delete',
    ]),
    ...mapActions('general/settings/roadcubeMarket/attributes', ['getAttributes']),

    openAttributeOptionDialog(mode, item) {
      this.mode = mode;
      this.attributeOption = cloneDeep(item);
      this.attributeOptionDialog = true;
    },

    handleSearch() {
      if (this.page != 1) {
        this.page = 1;
      } else {
        this.getAttributeOptions(`?q=${this.search}`);
      }
    },

    async submitForm() {
      let item = cloneDeep(this.attributeOption);
      let res = await this[this.mode](item);
      if (res) {
        await this.getAttributeOptions();
        this.closeForm();
      }
    },

    async deleteItem() {
      let item = cloneDeep(this.attributeOption);
      let res = await this.delete(item.attributeOption_id);
      if (res) {
        await this.getAttributeOptions();
        this.closeForm();
      }
    },
    closeForm() {
      this.changeNameElAuto = true;
      this.changeNameItAuto = true;
      this.changeNameEnAuto = true;
      this.changeDescriptionElAuto = true;
      this.changeDescriptionItAuto = true;
      this.changeDescriptionEnAuto = true;
      this.search = '';
      this.attributeOptionDialog = false;
      this.deleteDialog = false;
    },
    getAttributeName(id) {
      let attribute = this.attributes.find((x) => x.attribute_id == id);
      return attribute?.name[this.lang] || '-';
    },
  },

  watch: {
    $route(val) {
      if (!val.query.page) {
        this.$router.replace({
          query: {
            page: 1,
            ...this.$route.query,
          },
        });
      }
      this.getAttributeOptions(`${this.query}&q=${this.search}`);
    },

    page(page) {
      this.$router.replace({ query: { ...this.$route.query, page } });
    },

    search(val) {
      if (val == null) {
        this.getAttributeOptions(this.query);
      } else {
        this.debouncedSearch();
      }
    },

    ['$route.params.lang'](val) {
      this.nameLang = val;
    },

    ['attributeOption.option.en'](val) {
      if (this.lang == 'en') {
        if (this.changeNameElAuto) {
          this.attributeOption.option.el = val;
        }

        if (this.changeNameItAuto) {
          this.attributeOption.option.it = val;
        }

        this.changeNameEnAuto = false;
      } else if (val != this.attributeOption.option[this.lang]) {
        this.changeNameEnAuto = false;
      }
    },

    ['attributeOption.option.el'](val) {
      if (this.lang == 'el') {
        if (this.changeNameEnAuto) {
          this.attributeOption.option.en = val;
        }

        if (this.changeNameItAuto) {
          this.attributeOption.option.it = val;
        }

        this.changeNameElAuto = false;
      } else if (val != this.attributeOption.option[this.lang]) {
        this.changeNameElAuto = false;
      }
    },

    ['attributeOption.option.it'](val) {
      if (this.lang == 'it') {
        if (this.changeNameElAuto) {
          this.attributeOption.option.el = val;
        }

        if (this.changeNameEnAuto) {
          this.attributeOption.option.en = val;
        }

        this.changeNameItAuto = false;
      } else if (val != this.attributeOption.option[this.lang]) {
        this.changeNameItAuto = false;
      }
    },
  },

  beforeCreate() {
    if (!this.$route.query.page) {
      this.$router.replace({
        query: {
          page: 1,
          ...this.$route.query,
        },
      });
    }
  },

  created() {
    this.debouncedSearch = debounce(this.handleSearch, 500);
  },

  mounted() {
    this.getAttributeOptions(this.query);
    if (
      !this.attributes?.length ||
      this.attributes[0]?.store_id !== localStorage.getItem('storeId')
    ) {
      this.attributes = [];
      this.getAttributes();
    }
  },
};
</script>
<style>
.csv-input * {
  cursor: pointer;
}
</style>
